/**
 * useP2P Hook
 *
 * Manages the libp2p node lifecycle and connection state.
 * Also integrates Bluetooth mesh as a tertiary transport.
 * Includes LoRa mesh (Meshtastic/MeshCore) as quaternary transport.
 *
 * ⚠️ LoRa integration is EXPERIMENTAL.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type { Libp2p } from 'libp2p'
import type { P2PStatus, ConnectionStatus, TransportType, AllowedTransportsConfig } from '@/lib/types'
import {
  createP2PNode,
  getP2PNode,
  stopP2PNode,
  getConnectedPeerCount,
  isConnected,
  setupMessageHandler,
} from '@/lib/p2p'
import {
  isBluetoothAvailable,
  initializeBluetooth,
  getBluetoothStatus,
  startScanning as btStartScanning,
  getConnectedPeers as btGetConnectedPeers,
  disconnectAll as btDisconnectAll,
  stopBluetooth,
} from '@/lib/bluetooth/mesh'
import {
  isLoRaAvailable,
  getLoRaDevice,
  connectLoRa,
  disconnectLoRa,
  isLoRaConnected,
  getAvailableBackends,
} from '@/lib/lora'
import type { LoRaBackend, LoRaDevice } from '@/lib/lora'

export interface UseP2POptions {
  /** Allowed transport types for this room (optional - defaults to all) */
  allowedTransports?: AllowedTransportsConfig
}

export interface UseP2PReturn {
  node: Libp2p | null
  status: P2PStatus
  isInitialized: boolean
  error: Error | null
  initialize: () => Promise<void>
  shutdown: () => Promise<void>
  // Bluetooth controls
  bluetoothAvailable: boolean
  bluetoothEnabled: boolean
  enableBluetooth: () => Promise<void>
  disableBluetooth: () => void
  scanBluetooth: () => Promise<void>
  // LoRa controls (EXPERIMENTAL)
  loraAvailable: boolean
  loraDevice: LoRaDevice | null
  loraBackends: LoRaBackend[]
  connectLoRa: (backend?: LoRaBackend) => Promise<void>
  disconnectLoRa: () => void
  // Transport restrictions
  allowedTransports: AllowedTransportsConfig
  setAllowedTransports: (config: AllowedTransportsConfig) => void
}

const initialStatus: P2PStatus = {
  status: 'disconnected',
  activeTransport: null,
  peerCount: 0,
  webrtcConnected: false,
  websocketConnected: false,
  bluetoothConnected: false,
  loraConnected: false,
}

const DEFAULT_TRANSPORTS: AllowedTransportsConfig = {
  wifi: true,
  bluetooth: true,
  cellular: true,
  lora: true,
}

export function useP2P(options: UseP2POptions = {}): UseP2PReturn {
  const [node, setNode] = useState<Libp2p | null>(null)
  const [status, setStatus] = useState<P2PStatus>(initialStatus)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [bluetoothAvailable, setBluetoothAvailable] = useState(false)
  const [bluetoothEnabled, setBluetoothEnabled] = useState(false)
  const [loraAvailable, setLoraAvailable] = useState(false)
  const [loraDevice, setLoraDevice] = useState<LoRaDevice | null>(null)
  const [loraBackends, setLoraBackends] = useState<LoRaBackend[]>([])
  const [allowedTransports, setAllowedTransports] = useState<AllowedTransportsConfig>(
    options.allowedTransports ?? DEFAULT_TRANSPORTS
  )
  const statusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Check Bluetooth and LoRa availability on mount
  useEffect(() => {
    setBluetoothAvailable(isBluetoothAvailable())
    setLoraAvailable(isLoRaAvailable())
    setLoraBackends(getAvailableBackends())
  }, [])

  const updateStatus = useCallback(() => {
    const currentNode = getP2PNode()
    const btStatus = getBluetoothStatus()
    const btPeers = btGetConnectedPeers()
    const btConnected = btStatus === 'connected' && btPeers.length > 0

    // Check LoRa connection status
    const loraConnected = isLoRaConnected()
    const currentLoraDevice = getLoRaDevice()
    if (currentLoraDevice) {
      setLoraDevice(currentLoraDevice)
    }

    if (!currentNode && !btConnected && !loraConnected) {
      setStatus({
        ...initialStatus,
        bluetoothConnected: btConnected,
        loraConnected,
        peerCount: btPeers.length,
      })
      return
    }

    const libp2pPeerCount = currentNode ? getConnectedPeerCount() : 0
    const libp2pConnected = currentNode ? isConnected() : false
    // LoRa counts as 1 "peer" for mesh connectivity purposes
    const loraPeerCount = loraConnected ? 1 : 0
    const totalPeerCount = libp2pPeerCount + btPeers.length + loraPeerCount

    // Determine connection status - connected if any transport works
    // For cross-device sync, we only need relay + 1 peer (2 connections)
    // Previously required > 2 peers which prevented "connected" status in 2-device scenarios
    let connectionStatus: ConnectionStatus = 'disconnected'
    if ((libp2pConnected && libp2pPeerCount >= 1) || (btConnected && btPeers.length >= 1) || loraConnected) {
      connectionStatus = 'connected'
    }

    // Determine active transport (prefer WebRTC, then WebSocket, then Bluetooth, then LoRa)
    let activeTransport: TransportType | null = null
    if (libp2pConnected) {
      activeTransport = 'webrtc' // Default assumption for libp2p
    } else if (btConnected) {
      activeTransport = 'bluetooth'
    } else if (loraConnected) {
      activeTransport = 'lora'
    }

    setStatus({
      status: connectionStatus,
      activeTransport,
      peerCount: totalPeerCount,
      webrtcConnected: libp2pConnected,
      websocketConnected: false, // Would need actual check
      bluetoothConnected: btConnected,
      loraConnected,
    })
  }, [])

  const initialize = useCallback(async () => {
    if (isInitialized) return

    try {
      setError(null)
      const p2pNode = await createP2PNode()
      setupMessageHandler(p2pNode)
      setNode(p2pNode)
      setIsInitialized(true)

      // Start status polling
      statusIntervalRef.current = setInterval(updateStatus, 2000)
      updateStatus()

      console.log('[useP2P] Node initialized')
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      console.error('[useP2P] Initialization failed:', error)
    }
  }, [isInitialized, updateStatus])

  const shutdown = useCallback(async () => {
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current)
      statusIntervalRef.current = null
    }

    // Shutdown all transports
    await stopP2PNode()
    if (bluetoothEnabled) {
      stopBluetooth()
      setBluetoothEnabled(false)
    }
    if (isLoRaConnected()) {
      disconnectLoRa()
      setLoraDevice(null)
    }

    setNode(null)
    setIsInitialized(false)
    setStatus(initialStatus)
    console.log('[useP2P] Node shutdown')
  }, [bluetoothEnabled])

  // Bluetooth control functions
  const enableBluetooth = useCallback(async () => {
    if (!bluetoothAvailable) {
      console.warn('[useP2P] Bluetooth not available')
      return
    }

    // Check if Bluetooth is allowed by room settings
    if (!allowedTransports.bluetooth) {
      console.warn('[useP2P] Bluetooth is disabled by room settings')
      return
    }

    try {
      const initialized = await initializeBluetooth()
      if (initialized) {
        setBluetoothEnabled(true)
        console.log('[useP2P] Bluetooth enabled')
        updateStatus()
      }
    } catch (err) {
      console.error('[useP2P] Failed to enable Bluetooth:', err)
    }
  }, [bluetoothAvailable, allowedTransports.bluetooth, updateStatus])

  const disableBluetooth = useCallback(() => {
    btDisconnectAll()
    stopBluetooth()
    setBluetoothEnabled(false)
    console.log('[useP2P] Bluetooth disabled')
    updateStatus()
  }, [updateStatus])

  const scanBluetooth = useCallback(async () => {
    if (!bluetoothEnabled) {
      console.warn('[useP2P] Bluetooth not enabled')
      return
    }

    try {
      await btStartScanning()
      updateStatus()
    } catch (err) {
      console.error('[useP2P] Bluetooth scan failed:', err)
    }
  }, [bluetoothEnabled, updateStatus])

  // LoRa control functions (EXPERIMENTAL)
  const connectToLoRa = useCallback(async (backend?: LoRaBackend) => {
    if (!loraAvailable) {
      console.warn('[useP2P] LoRa (Web Bluetooth) not available')
      return
    }

    // Check if LoRa is allowed by room settings
    if (!allowedTransports.lora) {
      console.warn('[useP2P] LoRa is disabled by room settings')
      return
    }

    try {
      const device = await connectLoRa(backend)
      setLoraDevice(device)
      console.log('[useP2P] LoRa connected:', device.name)
      updateStatus()
    } catch (err) {
      console.error('[useP2P] LoRa connection failed:', err)
    }
  }, [loraAvailable, allowedTransports.lora, updateStatus])

  const disconnectFromLoRa = useCallback(() => {
    disconnectLoRa()
    setLoraDevice(null)
    console.log('[useP2P] LoRa disconnected')
    updateStatus()
  }, [updateStatus])

  // Disable Bluetooth if it's turned off in allowed transports
  useEffect(() => {
    if (!allowedTransports.bluetooth && bluetoothEnabled) {
      console.log('[useP2P] Bluetooth disabled by transport settings')
      btDisconnectAll()
      stopBluetooth()
      setBluetoothEnabled(false)
    }
  }, [allowedTransports.bluetooth, bluetoothEnabled])

  // Disable LoRa if it's turned off in allowed transports
  useEffect(() => {
    if (!allowedTransports.lora && isLoRaConnected()) {
      console.log('[useP2P] LoRa disabled by transport settings')
      disconnectLoRa()
      setLoraDevice(null)
    }
  }, [allowedTransports.lora])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current)
      }
    }
  }, [])

  return {
    node,
    status,
    isInitialized,
    error,
    initialize,
    shutdown,
    // Bluetooth
    bluetoothAvailable,
    bluetoothEnabled,
    enableBluetooth,
    disableBluetooth,
    scanBluetooth,
    // LoRa (EXPERIMENTAL)
    loraAvailable,
    loraDevice,
    loraBackends,
    connectLoRa: connectToLoRa,
    disconnectLoRa: disconnectFromLoRa,
    // Transport restrictions
    allowedTransports,
    setAllowedTransports,
  }
}
